import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { toast } from "react-toastify";
import { PagesPaginateModel } from "../../../models/Paginate/PagesPaginate";
import { ToastSettings } from "../../../utils/ToastSettings";
import { SubsectionModel } from "../../../models/Articles/Subsections/SubsectionModel";
import { SubsectionsService } from "../../../services/Articles/Subsections/SubsectionsService";
import { useLocation } from "react-router-dom";

interface ContextProps {
  subsection: SubsectionModel | null;
  setSubsection: Dispatch<SetStateAction<SubsectionModel | null>>;
  subsections: SubsectionModel[];
  subsectionsOptions: any[];
  setSubsectionsOptions: Dispatch<SetStateAction<any[]>>;
  params: any;
  setParams: Dispatch<SetStateAction<any>>;
  pages: PagesPaginateModel;
  setPages: Dispatch<SetStateAction<PagesPaginateModel>>;
  isLoading: boolean;
  isLoadingModalSubsections: boolean;
  error: string;
  setError: Dispatch<SetStateAction<string>>;
  handleList: () => void;
  handleFetch: (data: any) => Promise<boolean>;
  handleDelete: (id: number) => Promise<boolean>;
  handleReactivate: (id: number) => Promise<boolean>;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  handleListSubsectionsOption: (subsections: any) => void;
  handleSortSubsections: (sections: any) => Promise<boolean>;
}

export const SubsectionsContext = createContext<ContextProps>(
  {} as ContextProps,
);

interface SubsectionsProviderProps {
  children: ReactNode;
}

export const SubsectionsProvider = ({ children }: SubsectionsProviderProps) => {
  const [subsection, setSubsection] = useState<SubsectionModel | null>(null);
  const [subsections, setSubsections] = useState<SubsectionModel[]>([]);
  const [subsectionsOptions, setSubsectionsOptions] = useState<any | null>(
    null,
  );
  const [params, setParams] = useState<any | null>(null);
  const [pages, setPages] = useState<PagesPaginateModel>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingModalSubsections, setIsLoadingModalSubsections] =
    useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const location = useLocation();
  const service = new SubsectionsService();

  const handleList = useCallback(async () => {
    if (
      !params ||
      (!!params?.withPaginate &&
        !location.pathname.includes("/articles/subsections/list"))
    ) {
      return false;
    }

    try {
      setIsLoading(true);
      const [_Response, _Error] = await service.list(params);
      setIsLoading(false);

      if (!!_Error) {
        return setError(_Response?.message || _Error);
      }

      setSubsections(_Response?.items);
      setPages(_Response?.pagination);
      return setError("");
    } catch (e) {
      setIsLoading(false);
      setSubsections([]);
      setPages({});
      setError("Não foi possível carregar as seções.");
    }
  }, [params]);

  const handleFetch = async (data: any) => {
    try {
      const [_Response, _Error, _Code, _Errors] = await service.fetch(data);
      if (!!_Error) {
        ToastSettings(
          _Response?.message || _Error,
          "top-right",
          "error",
          () => {},
          _Errors,
        );
        return false;
      }

      setError("");
      return true;
    } catch (e) {
      setError("Houve um erro ao salvar a seções");
      return false;
    }
  };

  const handleDelete = async (id: number) => {
    try {
      setIsLoading(true);
      const [_Response, _Error] = await service.delete(id);
      setIsLoading(false);

      if (!!_Error) {
        setError(_Response?.message || _Error);
        return false;
      }

      setError("");
      return true;
    } catch (e: any) {
      setIsLoading(false);
      setError(
        e.response?.data?.message || "Houve um erro ao deletar a subseções",
      );
      return false;
    }
  };

  const handleReactivate = async (id: number) => {
    try {
      setIsLoading(true);
      const [_Response, _Error] = await service.reactivate(id);
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);

      if (!!_Error) {
        setError(_Response?.message || _Error);
        return false;
      }

      setError("");
      return true;
    } catch (e: any) {
      setIsLoading(false);
      setError(
        e.response?.data?.message || "Houve um erro ao reativar a subseções",
      );
      return false;
    }
  };

  const handleListSubsectionsOption = useCallback(async (subsections: any) => {
    try {
      setIsLoadingModalSubsections(true);
      const [_Response, _Error] = await service.list(subsections);
      setIsLoadingModalSubsections(false);

      if (!!_Error) {
        return setError(_Response?.message || _Error);
      }

      setSubsectionsOptions(
        _Response?.items.map((item: SubsectionModel) => ({
          label: item.name,
          value: item.id,
        })),
      );
      return setError("");
    } catch (e) {
      setIsLoadingModalSubsections(false);
      setSubsectionsOptions([]);
      setError("Não foi possível carregar as subseções.");
    }
  }, []);

  const handleSortSubsections = async (sections: any) => {
    try {
      setIsLoadingModalSubsections(true);
      const [_Response, _Error] = await service.sort(sections);

      setTimeout(() => {
        setIsLoadingModalSubsections(false);
      }, 1000);

      if (!!_Error) {
        setError(_Response?.message || _Error);
        return false;
      }

      setError("");
      return true;
    } catch (e: any) {
      setIsLoadingModalSubsections(false);
      setError(
        e.response?.data?.message || "Houve um erro ao ordenar Subseções",
      );
      return false;
    }
  };

  useEffect(() => {
    !!error && toast.error(error);
  }, [error]);

  useEffect(() => {
    handleList().then();
  }, [handleList]);

  useEffect(() => {
    setSubsectionsOptions(
      subsections?.map((item: SubsectionModel) => ({
        label: item.name,
        value: item.id,
      })),
    );
  }, [subsections]);

  return (
    <SubsectionsContext.Provider
      value={{
        subsectionsOptions,
        setSubsectionsOptions,
        subsection,
        setSubsection,
        subsections,
        params,
        setParams,
        pages,
        setPages,
        setIsLoading,
        isLoading,
        isLoadingModalSubsections,
        error,
        setError,
        handleList,
        handleFetch,
        handleDelete,
        handleReactivate,
        handleListSubsectionsOption,
        handleSortSubsections,
      }}
    >
      {children}
    </SubsectionsContext.Provider>
  );
};

export const useSubsections = () => useContext(SubsectionsContext);
